import { CloseOutlined } from '@ant-design/icons';
import { Input } from '@mm/components';
import { useDebounce } from '@mm/hooks';
import { MyLeadsCreateAudienceRequestParams } from '@mm/state/interfaces/myLeads';
import React, { useEffect, useState } from 'react';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { CheckIcon } from '../../../../../../common/component-icons/CheckIcon/CheckIcon';
import { CloseModalProps } from '../../../../../../common/modals/modal-helpers';
import { colors } from '../../../styles/colors';
import { useAudienceNameValidation } from '../../Audiences/use-audience-name-validation';
import { useCreateMyLeadsAudience } from '../use-create-my-leads-audience';
import styles from './CreateMyLeadsAudienceModal.module.css';

export const CreateMyLeadsAudienceModal = ({
  params,
  closeModal,
}: { params: MyLeadsCreateAudienceRequestParams } & CloseModalProps) => {
  const { createMyLeadsAudience } = useCreateMyLeadsAudience();
  const [audienceName, setAudienceName] = useState('');
  const trimmedNewAudienceName = audienceName.trim();
  const debouncedNewAudienceName = useDebounce(trimmedNewAudienceName, 1000);
  const { validateAudienceName } = useAudienceNameValidation();
  const [valid, setValid] = useState(false);
  const [checking, setChecking] = useState(false);

  const onClickDone = () => {
    const updatedParams = {
      ...params,
      name: audienceName,
    };
    createMyLeadsAudience({ params: { ...updatedParams } });
    closeModal();
  };

  useEffect(() => {
    setValid(false);
    setChecking(!!trimmedNewAudienceName);
  }, [trimmedNewAudienceName]);

  useEffect(() => {
    (async () => {
      setChecking(true);
      const valid = await validateAudienceName(debouncedNewAudienceName);
      setValid(valid);
      setChecking(false);
    })();
  }, [debouncedNewAudienceName, validateAudienceName]);

  return (
    <div className={styles.container}>
      <CloseOutlined
        className={styles.closeIcon}
        onClick={() => closeModal()}
      />
      <div className={styles.header}>
        <div className={styles.title}>Name your new Audience</div>
      </div>
      <div className={styles.audienceNameWrapper}>
        <Input
          css={{
            input: styles.audienceEditBarInput,
            container: styles.audienceEditBar,
          }}
          value={audienceName}
          onChange={e => setAudienceName(e.target.value)}
          invalid={!valid && !checking}
          isLoading={checking}
          icon={
            valid && !checking && trimmedNewAudienceName ? (
              <CheckIcon color={colors.green} size={16} />
            ) : undefined
          }
          placeholder="Enter name here..."
        />
        {!valid && !checking && (
          <div className={styles.error}>
            An audience with that name already exists
          </div>
        )}
      </div>
      <div className={styles.buttons}>
        <PrimaryButton
          onClick={onClickDone}
          className={styles.primaryBtn}
          disabled={!valid || !audienceName}
        >
          Save
        </PrimaryButton>
      </div>
    </div>
  );
};
